import { computeRotationAcceptedRanges, computeTiltAcceptedRanges, computeCompassAcceptedRanges, withLegacyToleranceNames, type CompassInterval } from '@agavi/blocks';

import type { HeadingBand, PlanTarget } from './sensor-plan-view';
import type { RotationBand } from './sensor-rotation-view';
import type { Block } from '@/types/experience';

/**
 * What the task has already told us about where a phone should be, and which way it should point.
 *
 * The whole reason a teacher never has to type a coordinate: they authored every target when they
 * built the blocks. This reads them back out so the simulator can draw them and the chips can name
 * them, and it is the only place that knows how a block's data becomes a shape on the plan view.
 *
 * Reading `block.data` loosely rather than through each block's `initData`: those normalise
 * authoring-time strings into a shape their own editors want, and pulling three of them in here
 * would couple the simulator to three editors. A target that has not been filled in yet is simply
 * skipped — the pin still has somewhere to be, and a half-authored block has nothing to draw.
 *
 * BUT LOOSELY IS NOT RAWLY, and this file learned the difference the hard way. A block saved
 * before the tolerance rename stores `errorValue` / `errorSymmetry`, not `tolerance*`; every
 * `initData` runs `withLegacyToleranceNames` for exactly that reason, and skipping their parse
 * meant skipping this too. The band simply vanished — a compass authored at 90° ± 10 drew no arc
 * at all, on a task sitting beside one authored after the rename that drew its band fine. Nothing
 * on screen said why, which is the failure mode that function's own note warns about.
 *
 * So every reader here goes through `dataOf` and none of them touches `block.data` directly.
 * Grading was never affected: the blocks hand the evaluator their PARSED data, which has already
 * been through the rename. This is the one path that reads the stored shape.
 */

/** A block's data under the CURRENT tolerance names, whatever names it was saved under. */
const dataOf = (b: Block): Record<string, unknown> =>
  withLegacyToleranceNames((b.data ?? {}) as Record<string, unknown>);

/**
 * A stored field as a number, or NaN when it has not been filled in.
 *
 * Deliberately NOT called `num`. Every block's own `num()` returns **0** for a blank, which is the
 * hazard `invariants.test.ts` ratchets against: an untouched answer field becomes a real value of
 * zero, so a fresh block advertises 0 as its correct answer. This one returns NaN precisely so a
 * blank stays absent — `computeCompassAcceptedRanges` already drops an answer that did not resolve,
 * and a target with no coordinate is skipped rather than drawn in the Atlantic.
 *
 * Sharing the name would make two functions with opposite behaviour indistinguishable at a glance,
 * and would hide these sites behind a ratchet that is watching for the other one.
 */
function parsedOrNaN(v: unknown): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : NaN;
}

/** A block's own name, or a fallback, so a chip can say "At Site A" rather than "At block 3". */
function nameOf(block: Block, fallback: string): string {
  return block.name?.trim() || fallback;
}

export function gpsTargetsIn(blocks: Block[], fallbackName: (n: number) => string): PlanTarget[] {
  const out: PlanTarget[] = [];
  blocks.filter(b => b.type === 'gps').forEach((b, i) => {
    const d = dataOf(b);
    const lat = parsedOrNaN(d.targetLat);
    const lng = parsedOrNaN(d.targetLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    const radius = parsedOrNaN(d.toleranceRadius);
    out.push({
      id: b.id,
      name: nameOf(b, fallbackName(i + 1)),
      at: { lat, lng },
      // A zero or missing radius still draws — as a dot rather than a circle — because the target
      // is real even when the tolerance has not been set yet.
      radiusMeters: Number.isNaN(radius) ? 0 : radius,
    });
  });
  return out;
}

/**
 * The accepted heading bands, from the same helper the compass block grades against.
 *
 * `computeCompassAcceptedRanges` is what `evaluateCompass` consults, so the arc drawn on the ring
 * is the arc the answer is actually checked against — not a second derivation free to disagree with
 * it. An answer row that has not resolved contributes no band, which that helper already handles.
 */
export function compassBandsIn(blocks: Block[], fallbackName: (n: number) => string): HeadingBand[] {
  const out: HeadingBand[] = [];
  blocks.filter(b => b.type === 'compass').forEach((b, i) => {
    const d = dataOf(b);
    /**
     * Coerced, because the app layer stores these as STRINGS.
     *
     * Compass keeps `value` and its tolerances as text while a teacher is typing — a half-entered
     * "9" has to survive on its way to "90" — and each block's own `initData` turns them into
     * numbers before grading. Passing the raw data through instead produced NaN bands, which
     * `computeCompassAcceptedRanges` correctly drops, so the ring simply drew nothing and looked
     * like a task with no answers rather than like a bug.
     */
    const asNum = (v: unknown) => {
      const n = parsedOrNaN(v);
      return Number.isNaN(n) ? 0 : n;
    };
    const answers = ((d.answers as Array<Record<string, unknown>>) ?? []).map(a => ({
      id: String(a.id ?? ''),
      value: parsedOrNaN(a.value),
      toleranceValue: asNum(a.toleranceValue),
      toleranceAbove: asNum(a.toleranceAbove),
      toleranceBelow: asNum(a.toleranceBelow),
    }));
    const betweenAnswers = ((d.betweenAnswers as Array<Record<string, unknown>>) ?? []).map(a => ({
      id: String(a.id ?? ''),
      low: parsedOrNaN(a.low),
      high: parsedOrNaN(a.high),
    }));
    let ranges: CompassInterval[] = [];
    try {
      ranges = computeCompassAcceptedRanges({
        conditionType: (d.conditionType as 'exact' | 'between') ?? 'exact',
        answers,
        betweenAnswers,
        toleranceSymmetry: (d.toleranceSymmetry as 'symmetric' | 'asymmetric') ?? 'symmetric',
      });
    } catch {
      // A half-authored block is not a reason to blank the whole simulator. Nothing to draw is the
      // right answer for one that cannot state its own accepted range yet.
      return;
    }
    ranges.forEach((r, j) => {
      if (!Number.isFinite(r.lo) || !Number.isFinite(r.hi)) return;
      out.push({
        id: `${b.id}-${j}`,
        name: nameOf(b, fallbackName(i + 1)),
        from: r.lo,
        to: r.hi,
      });
    });
  });
  return out;
}

/**
 * The accepted angle bands, from the same helper `evaluateRotation` grades against.
 *
 * Shaded on the dial so a teacher can drive the simulated phone ONTO and around the answer rather
 * than typing numbers at it and hoping. Same discipline as the compass ring: the arc drawn is the
 * arc the answer is checked against, not a second derivation free to disagree.
 *
 * One device drives every angle block in the task, so this returns every block's bands, named —
 * the same reason `gpsTargetsIn` returns a list rather than "the" target.
 */
export function rotationBandsIn(blocks: Block[], fallbackName: (n: number) => string): RotationBand[] {
  const out: RotationBand[] = [];
  blocks.filter(b => b.type === 'rotation').forEach((b, i) => {
    const d = dataOf(b);
    // Coerced for the same reason Compass's are: the app layer keeps these as strings while a
    // teacher types, and passing the raw data through produced NaN bands and a blank dial.
    const asNum = (v: unknown) => {
      const n = parsedOrNaN(v);
      return Number.isNaN(n) ? 0 : n;
    };
    const answers = ((d.answers as Array<Record<string, unknown>>) ?? []).map(a => ({
      id: String(a.id ?? ''),
      value: parsedOrNaN(a.value),
      toleranceValue: asNum(a.toleranceValue),
      toleranceAbove: asNum(a.toleranceAbove),
      toleranceBelow: asNum(a.toleranceBelow),
    }));
    const betweenAnswers = ((d.betweenAnswers as Array<Record<string, unknown>>) ?? []).map(a => ({
      id: String(a.id ?? ''),
      low: parsedOrNaN(a.low),
      high: parsedOrNaN(a.high),
    }));
    try {
      computeRotationAcceptedRanges({
        conditionType: (d.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
        answers,
        betweenAnswers,
        toleranceSymmetry: (d.toleranceSymmetry as 'symmetric' | 'asymmetric') ?? 'symmetric',
        threshold: asNum(d.threshold),
      }).forEach((r, j) => {
        if (!Number.isFinite(r.lo) || !Number.isFinite(r.hi)) return;
        out.push({ id: `${b.id}-${j}`, name: nameOf(b, fallbackName(i + 1)), from: r.lo, to: r.hi });
      });
    } catch {
      // A half-authored block is not a reason to blank the dial; it just has nothing to shade yet.
    }
  });
  return out;
}

/**
 * Tilt's shaded bands, and the measure to draw them against.
 *
 * Separate from `rotationBandsIn` for the reason the two blocks are separate: the accepted ranges
 * come from a different derivation on a different domain.
 */
export function tiltBandsIn(blocks: Block[], fallbackName: (n: number) => string): RotationBand[] {
  const out: RotationBand[] = [];
  blocks.filter(b => b.type === 'tilt').forEach((b, i) => {
    const d = dataOf(b);
    const asNum = (v: unknown) => {
      const n = parsedOrNaN(v);
      return Number.isNaN(n) ? 0 : n;
    };
    try {
      computeTiltAcceptedRanges({
        conditionType: (d.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
        answers: ((d.answers as Array<Record<string, unknown>>) ?? []).map(a => ({
          id: String(a.id ?? ''),
          value: parsedOrNaN(a.value),
          toleranceValue: asNum(a.toleranceValue),
          toleranceAbove: asNum(a.toleranceAbove),
          toleranceBelow: asNum(a.toleranceBelow),
        })),
        betweenAnswers: ((d.betweenAnswers as Array<Record<string, unknown>>) ?? []).map(a => ({
          id: String(a.id ?? ''), low: parsedOrNaN(a.low), high: parsedOrNaN(a.high),
        })),
        toleranceSymmetry: (d.toleranceSymmetry as 'symmetric' | 'asymmetric') ?? 'symmetric',
        threshold: asNum(d.threshold),
      }).forEach((r, j) => {
        if (!Number.isFinite(r.lo) || !Number.isFinite(r.hi)) return;
        out.push({ id: `${b.id}-${j}`, name: nameOf(b, fallbackName(i + 1)), from: r.lo, to: r.hi });
      });
    } catch {
      // A half-authored block has nothing to shade yet, which is not a reason to blank the dial.
    }
  });
  return out;
}

export function taskHasBlock(blocks: Block[], type: 'gps' | 'compass' | 'rotation' | 'tilt'): boolean {
  return blocks.some(b => b.type === type);
}
